// LockdownEngine — top-level orchestrator for the THREE NEW Layer 2
// browser-lockdown detectors:
//   * lockdown.devtools_detect
//   * lockdown.virtual_camera_detect
//   * lockdown.multi_monitor_detect
//
// NOTE: The pre-existing Layer 2 detectors (paste blocking, right-click
// blocking, fullscreen enforcement, tab-switch counter) already live
// inside iCode.jsx and predate the modular system. Phase 4 only adds the
// three NEW detectors here; the old ones continue to operate via their
// existing iCode handlers and will be migrated to this engine in a
// later sweep.
//
// Renders nothing — this is a pure side-effect component that mounts,
// starts the enabled detectors, emits cheating events for the lifetime
// of the test, and tears down on unmount.
//
// Props:
//   testId          : string  — required, links events to this test
//   applicationId?  : number  — optional, for dashboard joins
//   candidateEmail? : string  — optional
//   active?         : boolean — when false, pause all detectors (default true)

import { useEffect, useRef, useState } from 'react';
import { createEventEmitter } from '../Proctoring/eventEmitter';
import {
  useCheatingFeatureFlags,
  isCheatingFeatureEnabled,
  getCheatingFeatureThreshold,
} from '../Proctoring/useCheatingFeatureFlag';
import ProctoringWarning from '../Proctoring/ProctoringWarning';
import { fireCheatingDetectorEvent } from '../Snapshots/SnapshotEngine';
import { createDevtoolsDetector } from './detectors/devtoolsDetector';
import { createVirtualCameraDetector } from './detectors/virtualCameraDetector';
import { createMultiMonitorDetector } from './detectors/multiMonitorDetector';

// Feature keys this engine owns. Each detector is gated by its own flag
// so super-admins can toggle them independently.
const FEATURE_KEYS = [
  'lockdown.devtools_detect',
  'lockdown.virtual_camera_detect',
  'lockdown.multi_monitor_detect',
];


export default function LockdownEngine({
  testId,
  applicationId = null,
  candidateEmail = null,
  active = true,
}) {
  const { flags, loading: flagsLoading } = useCheatingFeatureFlags();
  const emitterRef = useRef(null);
  const detectorsRef = useRef([]);
  // Same warning-toast pattern as ProctoringEngine. Without this, lockdown
  // detector events go to the backend but the candidate sees nothing —
  // there's no visible feedback that DevTools / virtual cam / multi-mon
  // was caught. Top-right toast with a 6-second auto-dismiss.
  const [currentWarning, setCurrentWarning] = useState(null);

  // ── Build the event emitter once per (testId, applicationId) ────────────
  // Same emitter shape as ProctoringEngine. Two emitters running in the
  // same page is fine — both batch independently to the same backend
  // /api/cheating/events/bulk endpoint.
  useEffect(() => {
    if (!testId) return undefined;
    emitterRef.current = createEventEmitter({
      testId,
      applicationId,
      candidateEmail,
    });
    return () => {
      try {
        void emitterRef.current?.stop();
      } catch (_) { /* defensive */ }
      emitterRef.current = null;
    };
  }, [testId, applicationId, candidateEmail]);

  // ── Boot the detectors whose flags are enabled ──────────────────────────
  useEffect(() => {
    if (!active) return undefined;
    if (flagsLoading) return undefined;
    if (!emitterRef.current) return undefined;

    // If none of OUR sub-features are enabled, no-op. Cheap startup case
    // when a tenant has Layer 2 polish turned off.
    const anyEnabled = FEATURE_KEYS.some((k) => Boolean(flags?.[k]?.enabled));
    if (!anyEnabled) return undefined;

    // Shared emit function: POST to the backend AND surface a warning
    // toast for non-info events so the candidate gets immediate feedback
    // (and can self-correct, e.g. close DevTools). Info-level events go
    // to the log only — they're noise for the user.
    const emit = (event) => {
      try {
        emitterRef.current?.emit(event);
      } catch (_) { /* defensive */ }
      if (event?.severity && event.severity !== 'info') {
        setCurrentWarning(event);
        // Tell the SnapshotEngine to capture an evidence frame of the
        // exact moment this detector fired. No-op if event-triggered
        // snapshots are disabled in super-admin.
        fireCheatingDetectorEvent(event.feature_key, event.severity);
      }
      // eslint-disable-next-line no-console
      console.info('[lockdown] event emitted:', event?.feature_key,
        'severity=', event?.severity,
        'score=', event?.score_delta);
    };

    // eslint-disable-next-line no-console
    console.info('[lockdown] engine starting — enabled features:', {
      devtools: !!flags['lockdown.devtools_detect']?.enabled,
      virtual_camera: !!flags['lockdown.virtual_camera_detect']?.enabled,
      multi_monitor: !!flags['lockdown.multi_monitor_detect']?.enabled,
    });

    let cancelled = false;
    void (async () => {
      // ── DevTools detector ────────────────────────────────────────────
      if (flags['lockdown.devtools_detect']?.enabled) {
        try {
          const dev = createDevtoolsDetector({
            emit,
            getFlag: isCheatingFeatureEnabled,
            getThreshold: getCheatingFeatureThreshold,
          });
          dev.start();
          if (cancelled) { dev.stop(); return; }
          detectorsRef.current.push(dev);
          // eslint-disable-next-line no-console
          console.info('[lockdown] devtools detector started');
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('[lockdown] devtools detector init failed:', err?.message || err);
        }
      }

      // ── Virtual camera detector ──────────────────────────────────────
      if (flags['lockdown.virtual_camera_detect']?.enabled) {
        try {
          const vcam = createVirtualCameraDetector({
            emit,
            getFlag: isCheatingFeatureEnabled,
            getThreshold: getCheatingFeatureThreshold,
          });
          await vcam.start();
          if (cancelled) { vcam.stop(); return; }
          detectorsRef.current.push(vcam);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('[lockdown] virtual camera detector init failed:', err?.message || err);
        }
      }

      // ── Multi-monitor detector ───────────────────────────────────────
      if (flags['lockdown.multi_monitor_detect']?.enabled) {
        try {
          const multi = createMultiMonitorDetector({
            emit,
            getFlag: isCheatingFeatureEnabled,
            getThreshold: getCheatingFeatureThreshold,
          });
          await multi.start();
          if (cancelled) { multi.stop(); return; }
          detectorsRef.current.push(multi);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('[lockdown] multi-monitor detector init failed:', err?.message || err);
        }
      }
    })();

    return () => {
      cancelled = true;
      // Stop every detector we started. Each stop() is idempotent.
      while (detectorsRef.current.length > 0) {
        const d = detectorsRef.current.pop();
        try { d.stop(); } catch (_) { /* defensive */ }
      }
    };
    // We only re-run when `active` flips or the relevant flags change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    active,
    flagsLoading,
    flags?.['lockdown.devtools_detect']?.enabled,
    flags?.['lockdown.virtual_camera_detect']?.enabled,
    flags?.['lockdown.multi_monitor_detect']?.enabled,
  ]);

  // Render the shared warning toast component. The toast is fixed-
  // position (top-right, z-index 10000) — same as ProctoringEngine's
  // toast — so the two engines can coexist visually. If both fire at
  // exactly the same instant, whichever set state last wins for the
  // current 6-second display window (acceptable trade-off vs building
  // a global notification queue).
  return (
    <ProctoringWarning
      warning={currentWarning}
      onDismiss={() => setCurrentWarning(null)}
    />
  );
}
